import { useState } from "react";
import { createRoot } from "react-dom/client";
import type { HandlerFn } from "../../actions";
import { apiPost, type IRequestError } from "../../../../api";
import { Modal } from "../../../utils/modal";
import { toast } from "../../../toast";

function CreateDynamicGroupModal({
  url,
  rules,
  onClose,
}: {
  url: string;
  rules: string;
  onClose: () => void;
}) {
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async () => {
    const trimmed = name.trim();
    if (!trimmed) return;

    setSubmitting(true);
    setError("");

    try {
      const result = await apiPost<{ ok: boolean; name: string; url: string }>(url, { name: trimmed, rules });
      onClose();
      toast.success(`Группа «${result.name}» создана`, {
        description: <a href={result.url} style={{ color: "inherit", textDecoration: "underline" }}>Перейти к группе</a>,
      });
    } catch (e: unknown) {
      const err = e as IRequestError;
      setError(err.message || "Не удалось создать группу");
      setSubmitting(false);
    }
  };

  return (
    <Modal
      show={true}
      onClose={onClose}
      title="Создать динамическую группу"
      subtitle="Группа будет создана на основе текущего фильтра"
      size="sm"
      footer={
        <>
          <button
            className="rd-btn rd-btn--primary rd-btn--large"
            disabled={!name.trim() || submitting}
            onClick={handleSubmit}
          >
            {submitting ? "Создание..." : "Создать"}
          </button>
          <button
            className="rd-btn rd-btn--outline rd-btn--secondary rd-btn--large"
            onClick={onClose}
            disabled={submitting}
          >
            Отмена
          </button>
        </>
      }
    >
      <div className={`rd-input rd-input--large${error ? " rd-input--error" : ""}`}>
        <input
          type="text"
          className="rd-input__field"
          placeholder="Название группы"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && name.trim() && !submitting) handleSubmit();
          }}
          disabled={submitting}
          autoFocus
        />
        {error && <span className="rd-input__error-text">{error}</span>}
      </div>
    </Modal>
  );
}

export const createDynamicGroupHandler: HandlerFn = (ctx) => {
  const url = ctx.config?.url;
  if (!url) return;

  const rules = ctx.tableState?.filterRules || "{}";

  const container = document.createElement("div");
  document.body.appendChild(container);
  const root = createRoot(container);

  const cleanup = () => {
    try { root.unmount(); } catch { /* already unmounted */ }
    container.remove();
  };

  root.render(
    <CreateDynamicGroupModal url={url} rules={rules} onClose={cleanup} />,
  );
};
